// RBAC — 能力(capability)ベース。サーバ側で最終判定する（UI 出し分けはUXのみ）。

import type { Role, ApprovalAction } from "./types";

export const CAPABILITIES = [
  "users.manage", // ユーザー管理・権限変更
  "deal.create",
  "deal.read.all", // 全案件閲覧（スタッフは全員が全案件を閲覧可）
  "deal.read.own", // 自分の紹介案件のみ（partner）
  "deal.update", // 非金額の更新
  "deal.update.amount", // 金額の更新（ロック前）
  "deal.update.commission", // 紹介料（報酬ルール・予定額）の設定
  "deal.status.change",
  "deal.archive",
  "deal.hard_delete", // 物理削除
  "amount.unlock", // 金額ロック解除
  "partner.manage",
  "partner.read",
  "partner.read.own", // 自分（partner）
  "customer.manage",
  "customer.read",
  "vendor.manage", // 提案企業（ベンダー）の登録・編集
  "vendor.read",
  "sales_mgmt.read", // 営業管理（商談/営業報告/KPI）の閲覧（自分の分）
  "sales_mgmt.read.all", // 全スタッフの営業データ閲覧（管理層）。無い場合は自分の分のみ
  "sales_mgmt.manage", // 営業管理の編集（商談/報告）
  "sales_mgmt.config", // 営業設定（スタッフ/商談種別/報告項目の管理）= owner/admin のみ
  "bank.read", // 振込先閲覧
  "bank.read.own",
  "bank.change", // 振込先変更（承認制の起票）
  "commission_rule.manage",
  "finance.read", // 粗利/TUSG管理費/開発保守費の閲覧
  "file.upload",
  "file.replace",
  "file.delete",
  "file.read",
  "payment.confirm_in", // 入金確認の起票/実行
  "payment.pay_out", // 支払済み処理（承認後）
  "refund.manage",
  "closing.manage",
  "closing.reopen", // 月次締め解除
  "approval.view",
  "audit.read",
  "csv.export",
  "settings.manage",
  "operations.manage", // 総務業務課ページの編集・担当者閲覧（owner/finance のみ）
  "task.manage",
  "notification.read",
  // 補助金不正防止モジュール（C案件 / D紹介先 / 支払承認）
  "subsidy_case.read", // C案件の閲覧
  "subsidy_case.manage", // C案件の作成・編集
  "lead.read", // D紹介先・証拠の閲覧
  "lead.manage", // D紹介先・同意・アポ・商談の登録（tusg_staff相当）
  "lead_payment.approve", // BからCへの支払承認（tusg_admin相当）
  // 給与・支払通知書
  "exec_dashboard.read", // 経営ダッシュボードの閲覧（owner/finance のみ）
  "payroll.manage", // 給与計算（月次の変動項目入力・支払通知書の作成）= owner/finance
  "payroll.salary.manage", // 固定給マスタの設定・閲覧 = owner のみ
  "payroll.self", // 自分の支払通知書の閲覧（本人のみ・従業員向け）
  // 請求書支払管理（経理が請求書をアップロード → 社長が支払チェック）
  "invoice.manage", // 支払予定の請求書の登録・編集・削除（owner/finance/admin）
  "invoice.pay", // 支払済みチェック（社長＝owner のみ）
  // 採用（面接者）
  "interviewee.read", // 面接者の閲覧
  "interviewee.manage", // 面接者の登録・編集・判定
  // 資料格納（ドキュメントライブラリ・提案書）
  "document.read", // 資料・提案書の閲覧／ダウンロード
  "document.manage", // 資料・フォルダ・提案書の追加・編集・削除
  // ドメイン台帳（正本はスプレッドシート。ここは閲覧と自動確認）
  "domain.read",
] as const;
export type Capability = (typeof CAPABILITIES)[number];

const ALL: Capability[] = [...CAPABILITIES];

export const ROLE_CAPABILITIES: Record<Role, Capability[]> = {
  owner: ALL, // 全権
  admin: [
    "deal.create",
    "deal.read.all",
    "deal.update",
    "deal.update.amount",
    "deal.status.change",
    "deal.archive",
    "partner.manage",
    "partner.read",
    "customer.manage",
    "customer.read",
    "vendor.manage",
    "vendor.read",
    "sales_mgmt.read",
    "sales_mgmt.read.all",
    "sales_mgmt.manage",
    "sales_mgmt.config",
    "bank.read",
    "bank.change",
    "commission_rule.manage",
    "finance.read",
    "file.upload",
    "file.replace",
    "file.read",
    "payment.confirm_in",
    "refund.manage",
    "closing.manage",
    "approval.view",
    "audit.read",
    "csv.export",
    "settings.manage",
    "task.manage",
    "notification.read",
    "subsidy_case.read",
    "subsidy_case.manage",
    "lead.read",
    "lead.manage",
    "lead_payment.approve",
    "invoice.manage",
    "interviewee.read",
    "interviewee.manage",
    "document.read",
    "document.manage",
    "domain.read",
    "payroll.self", // 自分の支払通知書のみ閲覧可
  ],
  finance: [
    "deal.read.all",
    "deal.update.amount",
    "partner.read",
    "customer.read",
    "customer.manage", // 顧客・法人の編集、日々連絡設定、まとめ操作（owner/admin/sales と同様に可）
    "vendor.read",
    "sales_mgmt.read",
    "sales_mgmt.read.all",
    "bank.read", // 必要範囲のみ（行レベルで更にフィルタ）
    "bank.change",
    "commission_rule.manage",
    "finance.read",
    "file.upload",
    "file.replace",
    "file.read",
    "payment.confirm_in",
    "payment.pay_out", // ただし承認後のみ（サービス層で承認検査）
    "refund.manage",
    "closing.manage",
    "approval.view",
    "audit.read",
    "csv.export",
    "task.manage",
    "notification.read",
    "subsidy_case.read",
    "lead.read",
    "lead_payment.approve",
    "exec_dashboard.read", // 経営ダッシュボード（owner と経理のみ）
    "payroll.manage", // 給与計算・支払通知書（固定給の設定/閲覧は不可＝owner専用）
    "operations.manage", // 総務業務課の編集・担当者閲覧（owner/finance のみ）
    "invoice.manage", // 請求書支払の登録・編集（経理。支払チェックは社長=owner のみ）
    "document.read",
    "document.manage",
    "payroll.self", // 自分の支払通知書のみ閲覧可
  ],
  sales: [
    "deal.create",
    "deal.read.all", // スタッフは全案件を閲覧可
    "deal.update", // 非確定情報のみ（金額確定/入金/支払は不可）
    "deal.update.commission", // 紹介料（報酬ルール・予定額）は設定可。確定額は不可。
    "deal.status.change",
    "partner.manage",
    "partner.read",
    "customer.manage",
    "customer.read",
    "vendor.read",
    "sales_mgmt.read",
    "sales_mgmt.manage",
    "file.upload",
    "file.read",
    "csv.export", // 自分の範囲のみ（行レベル）
    "task.manage",
    "notification.read",
    "subsidy_case.read",
    "lead.read",
    "lead.manage", // tusg_staff相当: D証拠の登録は可。支払承認は不可。
    "document.read",
    "document.manage", // 提案書の作成・編集（営業が商談で使う）
    "payroll.self", // 自分の支払通知書のみ閲覧可
  ],
  partner: [
    "deal.read.own",
    "partner.read.own",
    "bank.read.own",
    "bank.change", // 自分の振込先（承認制の起票）
    "file.read", // 自案件のみ（行レベル）
    "csv.export", // 自分の範囲のみ
    "notification.read",
  ],
  viewer: [
    "deal.read.all", // 重要情報は別途マスク
    "partner.read",
    "customer.read",
    "vendor.read",
    "sales_mgmt.read",
    "sales_mgmt.read.all",
    "file.read",
    "notification.read",
    "subsidy_case.read",
    "lead.read",
    "document.read",
  ],
  // 税理士：経費精算（requireRoleで別途許可）と 給与計算 のみ。他の業務データは見せない。
  tax: [
    "payroll.manage",
    "payroll.self",
    "file.read",
    "notification.read",
  ],
};

export function can(role: Role, cap: Capability): boolean {
  return ROLE_CAPABILITIES[role]?.includes(cap) ?? false;
}

export function canAny(role: Role, caps: Capability[]): boolean {
  return caps.some((c) => can(role, c));
}

// どのロールが、どの承認アクションを「決裁」できるか。
// 重要承認は owner のみ。一部の確認は admin も可。
export const APPROVAL_AUTHORITY: Record<ApprovalAction, Role[]> = {
  activate_deal: ["owner", "admin"],
  confirm_contract_amount: ["owner"],
  confirm_commission: ["owner"],
  confirm_tusg_fee: ["owner"],
  confirm_dev_fee: ["owner"],
  confirm_payment_in: ["owner", "admin"],
  confirm_eligibility: ["owner"],
  mark_paid_out: ["owner"],
  unlock_amount: ["owner"],
  change_after_closing: ["owner"],
  change_bank_account: ["owner", "admin"],
  delete_file: ["owner"],
  hard_delete: ["owner"],
  refund_or_cancel: ["owner"],
  duplicate_payment_exception: ["owner"],
};

export function canDecideApproval(role: Role, action: ApprovalAction): boolean {
  return APPROVAL_AUTHORITY[action]?.includes(role) ?? false;
}

// 金額に関わる秘匿情報（粗利/TUSG/開発保守費）を見せてよいか。
export function canSeeFinancials(role: Role): boolean {
  return can(role, "finance.read");
}
